import { create } from 'zustand';
import type { PhaseType } from '@beat-games/game-config';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface CreditsState {
  creditsRemaining: number;
  freeRemaining: number;
  totalCredits: number;
  expiresAt: string | null;
  timeUntilExpiry: number;
  currentPhase: PhaseType;
  isLoading: boolean;
  isPurchasing: boolean;
  error: string | null;
}

interface CreditsStore extends CreditsState {
  fetchCredits: (wallet: string) => Promise<void>;
  purchaseCredits: (wallet: string, packs: number, sendTransaction: (treasury: string, amount: number) => Promise<string>) => Promise<boolean>;
  consumeCredit: () => void;
  reset: () => void;
}

const initialState: CreditsState = {
  creditsRemaining: 0,
  freeRemaining: 0,
  totalCredits: 0,
  expiresAt: null,
  timeUntilExpiry: 0,
  currentPhase: 'QUALIFIER',
  isLoading: false,
  isPurchasing: false,
  error: null,
};

export const useCreditsStore = create<CreditsStore>((set, get) => ({
  ...initialState,

  fetchCredits: async (wallet: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/api/credits?wallet=${wallet}`);
      const data = await response.json();

      if (data.success) {
        set({
          creditsRemaining: data.data.creditsRemaining,
          freeRemaining: data.data.freeRemaining,
          totalCredits: data.data.totalCredits,
          expiresAt: data.data.expiresAt,
          timeUntilExpiry: data.data.timeUntilExpiry,
          isLoading: false,
        });
      } else {
        set({ error: data.error?.message || 'Failed to fetch credits', isLoading: false });
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      set({ error: 'Failed to fetch credits', isLoading: false });
    }
  },

  purchaseCredits: async (wallet: string, packs: number, sendTransaction) => {
    set({ isPurchasing: true, error: null });

    try {
      // Step 1: Get purchase details
      const initResponse = await fetch(`${API_BASE}/api/credits/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet, packs }),
      });
      const initData = await initResponse.json();

      if (!initData.success) {
        set({ error: initData.error?.message || 'Failed to initiate purchase', isPurchasing: false });
        return false;
      }

      const { treasuryWallet, solAmount } = initData.data;

      // Step 2: Send SOL transaction
      const signature = await sendTransaction(treasuryWallet, solAmount);

      // Step 3: Confirm purchase
      const confirmResponse = await fetch(`${API_BASE}/api/credits/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet, signature, packs }),
      });
      const confirmData = await confirmResponse.json();

      if (confirmData.success) {
        set({
          creditsRemaining: confirmData.data.creditsRemaining,
          freeRemaining: confirmData.data.freeRemaining,
          totalCredits: confirmData.data.totalCredits,
          expiresAt: confirmData.data.expiresAt,
          timeUntilExpiry: confirmData.data.timeUntilExpiry,
          isPurchasing: false,
        });
        return true;
      } else {
        set({ error: confirmData.error?.message || 'Failed to confirm purchase', isPurchasing: false });
        return false;
      }
    } catch (error) {
      console.error('Failed to purchase credits:', error);
      set({ error: 'Failed to purchase credits', isPurchasing: false });
      return false;
    }
  },

  consumeCredit: () => {
    const state = get();
    if (state.freeRemaining > 0) {
      set({
        freeRemaining: state.freeRemaining - 1,
        totalCredits: state.totalCredits - 1,
      });
    } else if (state.creditsRemaining > 0) {
      set({
        creditsRemaining: state.creditsRemaining - 1,
        totalCredits: state.totalCredits - 1,
      });
    }
  },

  reset: () => set(initialState),
}));
